'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { SignupData } from './AuthPageClient';
import { createClient } from '@/lib/supabase/client';

interface Step2Data {
  timezone: string;
  wakeTime: string;
  sleepTime: string;
}

interface SignupStep2Props {
  signupData: Partial<SignupData>;
  referralCode?: string | null;
  onBack: () => void;
}

const languages = [
  { id: 'en', name: 'English', native: 'English', flag: '🇬🇧', rtl: false },
  { id: 'ar', name: 'Arabic', native: 'العربية', flag: '🇸🇦', rtl: true },
  { id: 'so', name: 'Somali', native: 'Soomaali', flag: '🇸🇴', rtl: false },
  { id: 'ur', name: 'Urdu', native: 'اردو', flag: '🇵🇰', rtl: true },
  { id: 'id', name: 'Indonesian', native: 'Bahasa Indonesia', flag: '🇮🇩', rtl: false },
];

const timezones = [
  'America/New_York (EST)',
  'America/Chicago (CST)',
  'America/Los_Angeles (PST)',
  'Europe/London (GMT)',
  'Europe/Paris (CET)',
  'Asia/Dubai (GST)',
  'Asia/Karachi (PKT)',
  'Asia/Dhaka (BST)',
  'Asia/Jakarta (WIB)',
  'Asia/Kuala_Lumpur (MYT)',
  'Africa/Mogadishu (EAT)',
  'Africa/Cairo (EET)',
];

async function recordReferralConversion(
  supabase: ReturnType<typeof createClient>,
  referralCode: string,
  userId: string,
  email: string
): Promise<{ ambassadorId: string; code: string } | null> {
  try {
    // Look up the referral code (public read policy allows this)
    const { data: codeRow, error: codeErr } = await supabase
      .from('referral_codes')
      .select('id, ambassador_id, is_active, max_uses, current_uses, expires_at')
      .eq('code', referralCode)
      .eq('is_active', true)
      .maybeSingle();

    if (codeErr || !codeRow) return null; // Code not found or inactive — silently skip

    // Check expiry
    if (codeRow.expires_at && new Date(codeRow.expires_at) < new Date()) return null;

    // Check max uses
    if (codeRow.max_uses !== null && codeRow.current_uses >= codeRow.max_uses) return null;

    // Insert conversion record
    const { error: insertErr } = await supabase
      .from('referral_conversions')
      .insert({
        referral_code_id: codeRow.id,
        ambassador_id: codeRow.ambassador_id,
        referred_user_id: userId,
        referred_email: email,
        plan_tier: 'free',
        plan_amount: 0.00,
        discount_applied: 0.00,
        status: 'pending',
      });

    if (insertErr) {
      console.log('Referral conversion insert error:', insertErr.message);
      return null;
    }

    // Increment current_uses on the referral code
    await supabase
      .from('referral_codes')
      .update({ current_uses: codeRow.current_uses + 1 })
      .eq('id', codeRow.id);

    // Increment total_referrals on ambassador profile
    await supabase.rpc('increment_ambassador_referrals', {
      p_ambassador_id: codeRow.ambassador_id,
    }).then(() => {}); // best-effort, ignore if RPC doesn't exist yet

    return { ambassadorId: codeRow.ambassador_id, code: referralCode };
  } catch (err) {
    console.log('Referral recording skipped:', err);
    return null;
  }
}

async function notifyAmbassadorOfSignup(
  supabase: ReturnType<typeof createClient>,
  ambassadorId: string,
  referralCode: string,
  newUserName: string
): Promise<void> {
  try {
    // Fetch ambassador's user_id and total_referrals
    const { data: ambassador } = await supabase
      .from('ambassador_profiles')
      .select('user_id, total_referrals')
      .eq('id', ambassadorId)
      .maybeSingle();

    if (!ambassador) return;

    // Fetch ambassador's email from user_profiles
    const { data: userProfile } = await supabase
      .from('user_profiles')
      .select('email')
      .eq('id', ambassador.user_id)
      .maybeSingle();

    if (!userProfile?.email) return;

    // Fire the notification email via the API route
    await fetch('/api/email/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'referral_signup',
        to: userProfile.email,
        data: {
          new_user_name: newUserName || 'A new member',
          referral_code: referralCode,
          total_referrals: (ambassador.total_referrals || 0) + 1,
        },
      }),
    });
  } catch (err) {
    console.log('Ambassador signup notification skipped:', err);
  }
}

export default function SignupStep2({ signupData, referralCode, onBack }: SignupStep2Props) {
  const [selectedLang, setSelectedLang] = useState('en');
  const [isLoading, setIsLoading] = useState(false);
  const [emailConfirmationPending, setEmailConfirmationPending] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Step2Data>({
    defaultValues: {
      timezone: 'Europe/London (GMT)',
      wakeTime: '07:00',
      sleepTime: '23:00',
    },
  });

  const onSubmit = async (data: Step2Data) => {
    setIsLoading(true);
    const supabase = createClient();

    try {
      // Create Supabase auth account
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: signupData.email || '',
        password: signupData.password || '',
        options: {
          data: {
            full_name: signupData.name || '',
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (signUpError) {
        toast.error(signUpError.message);
        setIsLoading(false);
        return;
      }

      const newUserId = authData?.user?.id;
      const newUserEmail = signupData.email || '';

      // Send confirmation email via Resend (bypasses Supabase's unreliable built-in email)
      fetch('/api/email/send-signup-confirmation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newUserEmail,
          fullName: signupData.name || '',
        }),
      }).catch(() => {
        // Fire-and-forget — don't block signup if email fails
      });

      // Insert user profile
      if (newUserId) {
        await supabase.from('user_profiles').upsert({
          id: newUserId,
          email: newUserEmail,
          full_name: signupData.name || '',
          language: selectedLang,
          timezone: data.timezone,
          wake_time: data.wakeTime,
          sleep_time: data.sleepTime,
        }, { onConflict: 'id' });

        // Record referral conversion if a code was captured
        const codeToUse = referralCode || sessionStorage.getItem('referral_code');
        if (codeToUse) {
          const conversionResult = await recordReferralConversion(supabase, codeToUse, newUserId, newUserEmail);
          sessionStorage.removeItem('referral_code');
          // Notify ambassador immediately after successful conversion
          if (conversionResult) {
            notifyAmbassadorOfSignup(
              supabase,
              conversionResult.ambassadorId,
              conversionResult.code,
              signupData.name || 'A new member'
            ); // fire-and-forget — don't await to avoid blocking signup
          }
        }
      }

      // Show email confirmation pending screen
      setRegisteredEmail(newUserEmail);
      setEmailConfirmationPending(true);
    } catch (err: any) {
      toast.error(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Email confirmation pending screen
  if (emailConfirmationPending) {
    return (
      <div className="w-full max-w-md mx-auto text-center">
        <div className="flex flex-col items-center gap-5">
          {/* Icon */}
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-4xl">📧</span>
          </div>

          <div>
            <h1 className="text-2xl font-700 text-foreground mb-2">Check your inbox</h1>
            <p className="text-muted-foreground text-sm leading-relaxed">
              We sent a confirmation link to
            </p>
            <p className="text-primary font-600 text-sm mt-1 break-all">{registeredEmail}</p>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-left w-full">
            <div className="flex items-start gap-3">
              <span className="text-lg flex-shrink-0">🌙</span>
              <div className="space-y-1">
                <p className="text-sm font-600 text-foreground">Next steps</p>
                <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Open the email from Rafeeq</li>
                  <li>Click the <span className="font-600 text-foreground">Confirm your email</span> link</li>
                  <li>You'll be signed in automatically</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 w-full">
            <p className="text-xs text-amber-700 text-center">
              <span className="font-600">Didn't receive it?</span> Check your spam folder. The link expires in 24 hours.
            </p>
          </div>

          <p className="text-xs text-muted-foreground">
            Wrong email?{' '}
            <button
              type="button"
              onClick={() => {
                setEmailConfirmationPending(false);
                onBack();
              }}
              className="text-primary font-600 hover:opacity-80 transition-opacity duration-150"
            >
              Go back and correct it
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-8">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs font-700">
            <Icon name="CheckIcon" size={14} />
          </div>
          <span className="text-sm text-muted-foreground">Account</span>
        </div>
        <div className="flex-1 h-px bg-primary" />
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-700">2</div>
          <span className="text-sm font-600 text-primary">Preferences</span>
        </div>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-700 text-foreground mb-2">Set your preferences</h1>
        <p className="text-muted-foreground text-sm">Rafeeq will personalise your experience around these settings</p>
      </div>

      {/* Language Selection */}
      <div className="mb-6">
        <label className="block text-sm font-500 text-foreground mb-1.5">
          Preferred language
        </label>
        <p className="text-xs text-muted-foreground mb-3">AI responses and the interface will be in this language</p>
        <div className="grid grid-cols-1 gap-2">
          {languages.map((lang) => (
            <button
              key={`lang-${lang.id}`}
              type="button"
              onClick={() => setSelectedLang(lang.id)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-150 text-left active:scale-95 ${
                selectedLang === lang.id
                  ? 'border-primary bg-primary/5 ring-1 ring-primary/30' :'border-border bg-card hover:bg-muted'
              }`}
            >
              <span className="text-xl">{lang.flag}</span>
              <div className="flex-1">
                <span className={`text-sm font-600 ${selectedLang === lang.id ? 'text-primary' : 'text-foreground'}`}>
                  {lang.name}
                </span>
                {lang.native !== lang.name && (
                  <span
                    className="ml-2 text-sm text-muted-foreground"
                    dir={lang.rtl ? 'rtl' : 'ltr'}
                  >
                    {lang.native}
                  </span>
                )}
              </div>
              {selectedLang === lang.id && (
                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <Icon name="CheckIcon" size={12} className="text-white" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Timezone */}
        <div>
          <label htmlFor="timezone" className="block text-sm font-500 text-foreground mb-1.5">
            Your timezone
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">Used to schedule your morning alarm and evening check-in</p>
          <select
            id="timezone"
            className="w-full bg-muted border border-border rounded-xl px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150"
            {...register('timezone', { required: 'Timezone is required' })}
          >
            {timezones.map((tz) => (
              <option key={`tz-${tz}`} value={tz}>{tz}</option>
            ))}
          </select>
        </div>

        {/* Wake/Sleep Times */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="wake-time" className="block text-sm font-500 text-foreground mb-1.5">
              Wake-up time
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">Morning alarm rings at this time</p>
            <input
              id="wake-time"
              type="time"
              className={`w-full bg-muted border rounded-xl px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150 ${
                errors.wakeTime ? 'border-red-400' : 'border-border'
              }`}
              {...register('wakeTime', { required: 'Wake-up time is required' })}
            />
            {errors.wakeTime && (
              <p className="mt-1 text-xs text-red-600">{errors.wakeTime.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="sleep-time" className="block text-sm font-500 text-foreground mb-1.5">
              Sleep time
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">Evening check-in prompt at this time</p>
            <input
              id="sleep-time"
              type="time"
              className={`w-full bg-muted border rounded-xl px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150 ${
                errors.sleepTime ? 'border-red-400' : 'border-border'
              }`}
              {...register('sleepTime', { required: 'Sleep time is required' })}
            />
            {errors.sleepTime && (
              <p className="mt-1 text-xs text-red-600">{errors.sleepTime.message}</p>
            )}
          </div>
        </div>

        {/* Info box */}
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <span className="text-lg flex-shrink-0">🌙</span>
            <p className="text-xs text-primary/80 leading-relaxed">
              Rafeeq will send a push notification at your sleep time asking about your day, then generate a personalised motivational video ready for your morning alarm.
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-5 py-3.5 rounded-xl border border-border text-sm font-600 text-foreground hover:bg-muted active:scale-95 transition-all duration-150"
          >
            <Icon name="ArrowLeftIcon" size={16} />
            Back
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 gradient-teal text-white rounded-xl px-4 py-3.5 text-sm font-600 hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ minHeight: '48px' }}
          >
            {isLoading ? (
              <>
                <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
                Creating your account...
              </>
            ) : (
              <>
                Start My Journey
                <span className="text-base">🚀</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}